import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SkillstemAttribution } from "@/components/SkillstemAttribution";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target,
  Search,
  Building2,
  Users,
  AlertTriangle,
  Lightbulb,
  Rocket,
  BookOpen,
  BarChart3,
  GraduationCap,
  Briefcase,
  Heart,
  Mail,
} from "lucide-react";
import { useEffect } from "react";
import { FeedbackModule } from "@/components/quiz/FeedbackModule";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const About = () => {
  useEffect(() => {
    document.title = "About We-Help.in | A SkillSTEM Academy LLP Initiative";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "We-Help.in, an initiative of SkillSTEM Academy LLP, helps students after 10+2 discover free courses, skills, and career pathways."
      );
    }
    return () => {
      document.title = "We-Help.in | Free Career & Course Discovery for Students — A SkillSTEM Initiative";
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-16 md:py-24">
        <div className="container max-w-3xl text-center">
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-3xl md:text-5xl font-extrabold text-foreground leading-tight"
          >
            Helping Students Find the Right Career Path After 10+2
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.15 }}
            className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            We-Help.in is a free platform that helps students discover courses, skills, and career pathways based on their interests.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.25 }}>
            <SkillstemAttribution variant="block" className="mt-4" />
          </motion.div>
        </div>
      </section>

      <div className="container max-w-4xl py-12 md:py-16 space-y-16">
        {/* Mission */}
        <Section icon={Target} title="Our Mission">
          <ul className="space-y-3">
            {[
              "Improve employability for students after 10+2",
              "Make free and accessible learning opportunities visible",
              "Bridge the gap between education and jobs",
              "Support students in making informed career decisions",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-muted-foreground">
                <span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Section>

        {/* What We Do */}
        <Section icon={Search} title="What We-Help.in Does">
          <ul className="space-y-3">
            {[
              "Curates free courses from government and global platforms",
              "Provides career pathway guidance based on your interests",
              "Helps students compare programs side-by-side",
              "Offers simple tools like quizzes for direction",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-muted-foreground">
                <BookOpen className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 border border-border">
            We-Help.in is a <strong>discovery and guidance platform</strong>. We do not directly provide courses, certifications, or admissions. All listed programs are offered by their respective providers.
          </p>
        </Section>

        {/* About SkillSTEM */}
        <Section icon={Building2} title="About SkillSTEM Academy LLP">
          <p className="text-muted-foreground">
            We-Help.in is an initiative of{" "}
            <a href="https://skillstem.in" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">
              SkillSTEM Academy LLP
            </a>
            , an organization focused on employability, skills, and real-world learning.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: Briefcase, label: "Project & Portfolio Practice (PPP)" },
              { icon: GraduationCap, label: "STEM Education & Training" },
              { icon: BarChart3, label: "Career Readiness Programs" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 rounded-lg bg-muted/50 p-3 border border-border text-sm text-muted-foreground">
                <Icon className="h-4 w-4 text-primary shrink-0" />
                {label}
              </div>
            ))}
          </div>
        </Section>

        {/* Who It's For */}
        <Section icon={Users} title="Who This Platform Is For">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: GraduationCap, label: "Students after 10+2", desc: "Exploring career options and free courses" },
              { icon: Briefcase, label: "Job Seekers", desc: "Looking into skill-based career paths" },
              { icon: Heart, label: "Parents", desc: "Seeking guidance for their children's futures" },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="rounded-xl border border-border bg-card p-5 text-center space-y-2">
                <Icon className="h-8 w-8 text-primary mx-auto" />
                <h3 className="font-semibold text-foreground">{label}</h3>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Disclaimer */}
        <section>
          <div className="rounded-xl border-2 border-destructive/30 bg-destructive/5 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-destructive shrink-0" />
              <h2 className="text-xl font-bold text-foreground">Important Disclaimer</h2>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {[
                "We-Help.in is an informational and guidance platform.",
                "We do not provide courses, certifications, jobs, or admissions.",
                "Course details, fees, eligibility, and availability may change without notice.",
                "Information is based on publicly available sources and may not always be current.",
                "Users must verify details directly with official course providers before applying.",
                "We do not guarantee outcomes such as jobs, salaries, or placements.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Our Approach */}
        <Section icon={Lightbulb} title="Our Approach">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "Simple & Fast", desc: "Student-first experience with no clutter" },
              { label: "Clarity Over Complexity", desc: "Easy-to-understand guidance, no jargon" },
              { label: "Real-World Focus", desc: "Designed for practical career decision-making" },
            ].map(({ label, desc }) => (
              <div key={label} className="rounded-lg bg-muted/50 border border-border p-4 space-y-1">
                <h3 className="font-semibold text-foreground text-sm">{label}</h3>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center py-8"
        >
          <Rocket className="h-10 w-10 text-primary mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Start Your Career Journey</h2>
          <p className="mt-2 text-muted-foreground">Discover the path that's right for you — in under 2 minutes.</p>
          <Button asChild variant="cta" size="lg" className="mt-6">
            <Link to="/career-quiz">Take the Career Quiz</Link>
          </Button>
        </motion.section>

        {/* Quiet contact line for partners, volunteers, and feedback */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="pt-4"
        >
          <div className="rounded-lg border border-border bg-muted/30 p-4 md:p-5 text-center">
            <p className="text-sm text-muted-foreground inline-flex items-center justify-center gap-2 flex-wrap">
              <Mail className="h-4 w-4 text-primary shrink-0" />
              We-Help.in is in active development. For partnership, volunteer, or feedback enquiries, write to{" "}
              <a
                href="mailto:info@skillstem.in"
                className="text-primary font-medium hover:underline"
              >
                info@skillstem.in
              </a>
              .
            </p>
          </div>
        </motion.section>

        {/* Feedback module */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="pt-2"
        >
          <FeedbackModule sourcePage="/about" />
        </motion.section>
      </div>

      <Footer />
    </div>
  );
};

const Section = ({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) => (
  <motion.section variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <div className="flex items-center gap-3 mb-4">
      <Icon className="h-6 w-6 text-primary shrink-0" />
      <h2 className="text-xl md:text-2xl font-bold text-foreground">{title}</h2>
    </div>
    {children}
  </motion.section>
);

export default About;
